const API_BASE_URL = 'https://personalfinanceacademy.net/finance-api';
const LIBRARY_ACCESS_COOKIE = 'pfa_library_access';
const LIBRARY_ACCESS_COOKIE_MAX_AGE = 60 * 60 * 24 * 7;

// This cookie is deliberately only a routing hint for Apache. The API remains
// responsible for validating the bearer token and library entitlement.
export function setLibraryAccessCookie() {
  document.cookie = `${LIBRARY_ACCESS_COOKIE}=1; Max-Age=${LIBRARY_ACCESS_COOKIE_MAX_AGE}; Path=/; SameSite=Lax; Secure`;
}

export function clearLibraryAccessCookie() {
  document.cookie = `${LIBRARY_ACCESS_COOKIE}=; Max-Age=0; Path=/; SameSite=Lax; Secure`;
}


async function authRequest(path, payload) {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });

  const result = await response.json().catch(() => null);

  if (!response.ok || !result?.success) {
    throw new Error(result?.message || 'Something went wrong. Please try again.');
  }

  return result.data;
}

async function apiRequest(path, options = {}) {
  const token = localStorage.getItem('authToken');
  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    },
  });

  const result = await response.json().catch(() => null);

  if (!response.ok || !result?.success) {
    throw new Error(result?.message || 'Something went wrong. Please try again.');
  }

  return result.data;
}

export function loginUser(payload) {
  return authRequest('/auth/login', payload);
}

export function signupUser(payload) {
  return authRequest('/auth/signup', payload);
}

export function saveAuthSession(authData) {
  localStorage.setItem('authToken', authData.token);
  localStorage.setItem('authUser', JSON.stringify(authData.user));

  if (hasActiveSubscription(authData.user)) {
    localStorage.setItem('paymentSubmitted', 'true');
    setLibraryAccessCookie();
  } else {
    localStorage.removeItem('paymentSubmitted');
    clearLibraryAccessCookie();
  }
}

export function hasActiveSubscription(user) {
  const planId = user?.plan_id ?? user?.user_meta?.plan_id;
  const subscriptionStatus = user?.subscription_status ?? user?.user_meta?.subscription_status;

  return Boolean(planId) && String(subscriptionStatus).toLowerCase() === 'active';
}

export function getStoredAuthUser() {
  try {
    return JSON.parse(localStorage.getItem('authUser') || 'null');
  } catch {
    return null;
  }
}

export function hasPaidAccess() {
  return hasActiveSubscription(getStoredAuthUser())
    || localStorage.getItem('paymentSubmitted') === 'true';
}

export function clearAuthSession() {
  localStorage.removeItem('authToken');
  localStorage.removeItem('authUser');
  localStorage.removeItem('paymentSubmitted');
  clearLibraryAccessCookie();
}

export function getUserDashboard() {
  return apiRequest('/user/dashboard');
}

export function getPlans() {
  return apiRequest('/plans');
}

export function validateIban(payload) {
  return apiRequest('/subscriptions/validate-iban', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export function createPaymentSession(payload) {
  return apiRequest('/subscriptions/create-payment-session', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export function submitPayment(payload) {
  return apiRequest('/subscriptions/submit-payment', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export function getPaymentStatus(sessionToken) {
  return apiRequest(`/subscriptions/payment-status/${encodeURIComponent(sessionToken)}`);
}

export function getMyCurrentPlan() {
  return apiRequest('/subscriptions/my-plan');
}

export function getLibrary() {
  return apiRequest('/library');
}

export function updateUserProfile(payload) {
  return apiRequest('/user/profile', {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
}

export function changePassword(payload) {
  return apiRequest('/auth/change-password', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export function unsubscribeUser(payload) {
  return apiRequest('/subscriptions/unsubscribe', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}
